import { type FormEvent, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { captureFlowError, captureFlowStep } from '@/core/monitoring/sentry'
import { AuthEntryCard } from '@/features/auth/components/AuthEntryCard'
import { Button } from '@/shared/components/ui/Button'
import { passwordResetService } from '@/features/auth/services/passwordReset.service'
import { setPasswordResetStarted } from '@/features/auth/store/passwordReset.store'

export function ForgotPasswordEmailPage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['auth', 'validation'])

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const canSubmit = isEmailValid && !isSubmitting
  const emailFormatError = email.trim().length > 0 && !isEmailValid
    ? t('validation:email.invalid')
    : null

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!canSubmit) return

    setIsSubmitting(true)
    setErrorMessage(null)
    captureFlowStep('password-reset', 'email-submitted')

    let result
    try {
      result = await passwordResetService.start({ email: email.trim(), lang: i18n.language })
    } catch (err) {
      captureFlowError('password-reset', 'email-submitted', err)
      setIsSubmitting(false)
      setErrorMessage(t('auth:forgotPassword.email.error.generic'))
      return
    }

    setIsSubmitting(false)

    if (result.success) {
      captureFlowStep('password-reset', 'otp-sent')
      setPasswordResetStarted(
        result.data.correlationId,
        result.data.maskedEmail,
        result.data.expiresInSeconds,
        email.trim(),
      )
      navigate('/forgot-password/code')
      return
    }

    captureFlowStep('password-reset', 'email-failed', { errorCode: result.errorCode ?? 'unknown' })
    setErrorMessage(
      result.errorCode === 'ERR_PASSWORD_RESET_ACCOUNT_NOT_FOUND'
        ? t('auth:forgotPassword.email.error.notFound')
        : t('auth:forgotPassword.email.error.generic'),
    )
  }

  return (
    <AuthEntryCard variant="forgot">
      <div className="flex w-full flex-col items-center">
        <div className="mt-11 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
            {t('auth:forgotPassword.email.title')}
          </h1>
          <p className="mt-2 text-[0.8125rem] leading-6 text-text-muted">
            {t('auth:forgotPassword.email.description')}
          </p>
        </div>

        {errorMessage ? (
          <div
            aria-live="polite"
            className="mt-5 flex w-full max-w-96 items-start gap-2 rounded-lg bg-[#fde9eb] px-3 py-2 text-[0.8125rem] leading-[1.45] text-[#2f3640]"
            role="alert"
          >
            <span className="flex-1">{errorMessage}</span>
          </div>
        ) : null}

        <form className="mt-11 flex w-full max-w-96 flex-col gap-6" noValidate onSubmit={handleSubmit}>
          <div className="flex flex-col gap-3.5">
            <label className="text-[0.8125rem] leading-6 text-text-muted" htmlFor="reset-email">
              {t('auth:forgotPassword.email.label')}
            </label>
            <div className="relative flex items-center">
              <input
                autoComplete="email"
                className="min-h-[2.125rem] w-full rounded border border-border-soft bg-surface-card px-3 py-[0.4375rem] pr-9 text-[0.8125rem] leading-6 text-text-primary transition-[border-color,box-shadow] duration-150 placeholder:text-[#99a7b5] focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                id="reset-email"
                maxLength={254}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setErrorMessage(null)
                }}
                placeholder={t('auth:forgotPassword.email.placeholder')}
                required
                type="email"
                value={email}
              />
            </div>
            {emailFormatError ? (
              <p className="mt-1.5 text-sm leading-[1.4] text-[#e02d3c]" role="alert">
                {emailFormatError}
              </p>
            ) : null}
          </div>

          <Button className="min-h-9 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100" disabled={!canSubmit} fullWidth type="submit" variant="primary">
            {isSubmitting
              ? t('auth:forgotPassword.email.btn.submitting')
              : t('auth:forgotPassword.email.btn.submit')}
          </Button>

          <p className="mt-4 text-center text-[0.8125rem] text-text-muted">
            <Link className="text-xs font-medium text-brand-primary no-underline hover:underline" to="/login">
              {t('auth:forgotPassword.email.backToLogin')}
            </Link>
          </p>
        </form>
      </div>
    </AuthEntryCard>
  )
}
